import type { GifEditWorkerResponse } from './gifEditProtocol'
import {
  assertGifSourceByteLimit,
  GIF_EDIT_RENDER_TIMEOUT_MS,
  GifEditAdmissionError,
} from './limits'
import type { GifEditLimitReason } from './limits'
import type { GifEditMetadata } from './parseGif'
import type { GifEditRecipe } from './recipe'
import type { GifEditRenderOptions, GifEditRenderResult } from './renderGifEdit'

type PendingInspect = {
  kind: 'inspect'
  workerGeneration: number
  resolve: (metadata: GifEditMetadata) => void
  reject: (error: Error) => void
  cleanup: () => void
}

type PendingRender = {
  kind: 'render'
  workerGeneration: number
  resolve: (result: GifEditRenderResult) => void
  reject: (error: Error) => void
  onFrame?: (frameIndex: number) => void
  cleanup: () => void
}

type PendingRequest = PendingInspect | PendingRender

const cancelledMessage = 'GIF edit was cancelled'

const toArrayBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const buffer = new ArrayBuffer(bytes.byteLength)
  new Uint8Array(buffer).set(bytes)
  return buffer
}

const toClientError = (message: {
  errorName: string
  message: string
  reason?: GifEditLimitReason
}): Error => {
  if (message.errorName === 'AbortError') {
    return new DOMException(message.message, 'AbortError')
  }
  if (message.errorName === 'GifEditAdmissionError') {
    return new GifEditAdmissionError(
      message.reason ?? 'timeout',
      message.message,
    )
  }
  return new Error(message.message)
}

const abortError = (): DOMException =>
  new DOMException(cancelledMessage, 'AbortError')

export type GifEditBrowserClient = {
  inspect: (source: Uint8Array) => Promise<GifEditMetadata>
  render: (
    source: Uint8Array,
    recipe: GifEditRecipe,
    options?: GifEditRenderOptions,
  ) => Promise<GifEditRenderResult>
  dispose: () => void
}

export const createGifEditBrowserClient = (
  createWorker: () => Worker,
): GifEditBrowserClient => {
  let worker: Worker | null = createWorker()
  let nextId = 1
  let workerGeneration = 0
  let activeRenderId: number | null = null
  let disposed = false
  const pending = new Map<number, PendingRequest>()

  const bindWorker = (nextWorker: Worker): void => {
    const boundGeneration = workerGeneration
    nextWorker.addEventListener(
      'message',
      (event: MessageEvent<GifEditWorkerResponse>) => {
        if (boundGeneration !== workerGeneration) return
        const message = event.data
        const request = pending.get(message.requestId)
        if (!request || request.workerGeneration !== boundGeneration) return

        if (message.type === 'progress') {
          if (request.kind === 'render') {
            request.onFrame?.(message.frameIndex)
          }
          return
        }

        settle(message.requestId)
        if (message.type === 'inspected' && request.kind === 'inspect') {
          request.resolve(message.metadata)
          return
        }
        if (message.type === 'rendered' && request.kind === 'render') {
          request.resolve({
            bytes: new Uint8Array(message.bytes),
            stats: message.stats,
          })
          return
        }
        if (message.type === 'failed') {
          request.reject(toClientError(message))
          return
        }
        request.reject(new Error('GIF worker sent an unexpected response'))
      },
    )

    nextWorker.addEventListener('error', () => {
      if (boundGeneration !== workerGeneration) return
      stopWorker(new Error('GIF worker failed'))
    })
  }

  const settle = (requestId: number): PendingRequest | undefined => {
    const request = pending.get(requestId)
    if (!request) return undefined
    pending.delete(requestId)
    request.cleanup()
    if (activeRenderId === requestId) activeRenderId = null
    return request
  }

  const rejectRequest = (requestId: number, error: Error): void => {
    const request = settle(requestId)
    request?.reject(error)
  }

  const rejectOwnedRequests = (error: Error, generation: number): void => {
    for (const requestId of Array.from(pending.keys())) {
      const request = pending.get(requestId)
      if (request?.workerGeneration === generation) {
        rejectRequest(requestId, error)
      }
    }
  }

  const startWorker = (): Worker => {
    const nextWorker = createWorker()
    worker = nextWorker
    bindWorker(nextWorker)
    return nextWorker
  }

  const getWorker = (): Worker => worker ?? startWorker()

  const stopWorker = (error: Error): void => {
    const endedGeneration = workerGeneration
    rejectOwnedRequests(error, endedGeneration)
    worker?.terminate()
    worker = null
    activeRenderId = null
    workerGeneration += 1
  }

  const replaceWorker = (error: Error = abortError()): void => {
    stopWorker(error)
    if (disposed) return
    try {
      startWorker()
    } catch {
      worker = null
    }
  }

  const abortActiveRender = (): void => {
    let hasOwnedRender = activeRenderId !== null
    if (!hasOwnedRender) {
      for (const request of pending.values()) {
        if (
          request.kind === 'render' &&
          request.workerGeneration === workerGeneration
        ) {
          hasOwnedRender = true
          break
        }
      }
    }
    if (!hasOwnedRender) return
    replaceWorker()
  }

  bindWorker(worker)

  return {
    inspect(source) {
      assertGifSourceByteLimit(source.byteLength)
      if (disposed) return Promise.reject(abortError())
      let requestWorker: Worker
      try {
        requestWorker = getWorker()
      } catch (error) {
        return Promise.reject(error)
      }
      const requestId = nextId
      nextId += 1
      const generation = workerGeneration
      return new Promise<GifEditMetadata>((resolve, reject) => {
        pending.set(requestId, {
          kind: 'inspect',
          workerGeneration: generation,
          resolve,
          reject,
          cleanup: () => undefined,
        })
        const sourceBuffer = toArrayBuffer(source)
        requestWorker.postMessage(
          { type: 'inspect', requestId, source: sourceBuffer },
          [sourceBuffer],
        )
      })
    },
    render(source, recipe, options = {}) {
      assertGifSourceByteLimit(source.byteLength)
      if (disposed) return Promise.reject(abortError())
      if (options.signal?.aborted) {
        return Promise.reject(abortError())
      }

      abortActiveRender()
      let requestWorker: Worker
      try {
        requestWorker = getWorker()
      } catch (error) {
        return Promise.reject(error)
      }
      const requestId = nextId
      nextId += 1
      const generation = workerGeneration
      activeRenderId = requestId

      return new Promise<GifEditRenderResult>((resolve, reject) => {
        const onAbort = (): void => {
          replaceWorker()
        }
        options.signal?.addEventListener('abort', onAbort)

        const timeoutId = setTimeout(() => {
          const request = pending.get(requestId)
          if (request?.workerGeneration === generation) {
            replaceWorker(new GifEditAdmissionError('timeout'))
          }
        }, GIF_EDIT_RENDER_TIMEOUT_MS)

        pending.set(requestId, {
          kind: 'render',
          workerGeneration: generation,
          resolve,
          reject,
          onFrame: options.onFrame,
          cleanup: () => {
            clearTimeout(timeoutId)
            options.signal?.removeEventListener('abort', onAbort)
          },
        })

        if (options.signal?.aborted) {
          replaceWorker()
          return
        }

        if (pending.get(requestId)?.workerGeneration !== workerGeneration) {
          return
        }

        const sourceBuffer = toArrayBuffer(source)
        requestWorker.postMessage(
          {
            type: 'render',
            requestId,
            source: sourceBuffer,
            recipe,
          },
          [sourceBuffer],
        )
      })
    },
    dispose() {
      disposed = true
      const error = abortError()
      for (const requestId of Array.from(pending.keys())) {
        rejectRequest(requestId, error)
      }
      activeRenderId = null
      worker?.terminate()
      worker = null
    },
  }
}
